import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';
import { AxiosError } from 'axios';

export interface DashboardStatsDto {
  contractStats: ContractStatsDto;
  businessMetrics: BusinessMetricsDto;
  periodMetrics: PeriodMetricsDto;
  userPerformance: UserPerformanceDto[];
  teamPerformance: TeamPerformanceDto[];
}

export interface ContractStatsDto {
  contratosCriados: number;
  contratosAssinados: number;
  contratosEmAnalise: number;
  contratosAtivos: number;
  contratosSuspensos: number;
  contratosCancelados: number;
  contratosFinalizados: number;
  totalContratos: number;
  inputEasyVendas?: number;
  contratosSemAssinatura?: number;
  contratosSemAssinaturaMais24h?: number;
}

export interface BusinessMetricsDto {
  linhasNovas: number;
  renegociacoes: number;
  valorTotalMensal: number;
  comprometimentoMinimo: number;
  taxaConversao: number;
  tempoMedioProcessamento: number;
  totalLinhas: number;
  receitaAditivosCriados: number;
  receitaAditivosAtivos: number;
  receitaRenegociacoesCriados: number;
  receitaRenegociacoesAtivos: number;
}

export interface PeriodMetricsDto {
  hoje: PeriodDataDto;
  estaSemana: PeriodDataDto;
  esteMes: PeriodDataDto;
  ultimos7Dias: PeriodDataDto[];
  ultimos30Dias: PeriodDataDto[];
}

export interface PeriodDataDto {
  data: string;
  criados: number;
  assinados: number;
  ativados: number;
  total: number;
  crescimento: number;
  periodo: string;
}

export interface UserPerformanceDto {
  userId: string;
  userName: string;
  userEmail: string;
  totalContratos: number;
  contratosAtivos: number;
  contratosAssinados: number;
  valorTotalMensal: number;
  contratosHoje: number;
  contratosEstaSemana: number;
  contratosEsteMes: number;
  taxaConversao: number;
  teamName: string;
  isActive: boolean;
}

export interface TeamPerformanceDto {
  teamId: string;
  teamName: string;
  managerName: string;
  totalContratos: number;
  contratosAtivos: number;
  contratosAssinados: number;
  valorTotalMensal: number;
  contratosHoje: number;
  contratosEstaSemana: number;
  contratosEsteMes: number;
  taxaConversao: number;
  salespersonsCount: number;
  salespersons: UserPerformanceDto[];
}

export interface ContractStatusDistributionDto {
  status: string;
  statusName: string;
  count: number;
  percentage: number;
  color: string;
}

export interface BusinessMetricsChartDto {
  category: string;
  linhasNovas: number;
  renegociacoes: number;
  valorMensal: number;
}

export interface PeriodChartDto {
  data: string;
  contratosCriados: number;
  contratosAssinados: number;
  contratosAtivados: number;
  valorMensal: number;
}

export interface TopPerformersDto {
  topVendedores: UserPerformanceDto[];
  topValorMensal: UserPerformanceDto[];
  topConversao: UserPerformanceDto[];
}

export interface BkoDashboardStatsDto {
  cqvOk: number;
  criadoEasyVendas: number;
  inputEasyVendas: number;
}

export interface OperationalChartDto {
  data: string;
  criadoEasyVendas: number;
  inputEasyVendas: number;
}

export interface FinancialChartDto {
  data: string;
  aditivos: number;
  renegociacoes: number;
}

export interface StateTimeDistributionDto {
  estado: string;
  horario: string;
  quantidade: number;
  data: string;
}

export interface TopPerformersDetailDto {
  userId: string;
  userName: string;
  contratosCriados: number;
  contratosAssinados: number;
  contratosAprovados: number;
  taxaConversao: number;
}

export interface TopPerformersResponseDto {
  top5Melhor: TopPerformersDetailDto[];
  top5Pior: TopPerformersDetailDto[];
}

export interface TopTeamsDetailDto {
  teamId: string;
  teamName: string;
  managerName: string;
  contratosCriados: number;
  contratosAssinados: number;
  contratosAprovados: number;
  taxaConversao: number;
  salespersonsCount: number;
}

export interface TopTeamsResponseDto {
  top5Melhor: TopTeamsDetailDto[];
  top5Pior: TopTeamsDetailDto[];
}

export interface TeamSalespersonsDto {
  teamId: string;
  teamName: string;
  salespersons: TopPerformersDetailDto[];
}

export interface LineMetricByTypeDto {
  total: number;
  aditivo: number;
  renegociacao: number;
  novaLinha: number;
}

export interface LineStatsDto {
  linhasCriadas: LineMetricByTypeDto;
  linhasAprovadasCqv: LineMetricByTypeDto;
  linhasSuspensas: LineMetricByTypeDto;
  linhasContratosCancelados: LineMetricByTypeDto;
  valorTotalMensal: number;
}

export interface LineStatusDistributionDto {
  status: string;
  count: number;
  percentage: number;
  color: string;
}

export interface LinePeriodChartDto {
  data: string;
  linhasCriadas: number;
  linhasAtivadas: number;
  valorMensal: number;
}

export interface LineNegotiationDistributionDto {
  negociacao: string;
  count: number;
  percentage: number;
  color: string;
}

export interface LinesDashboardDto {
  stats: LineStatsDto;
  statusDistribution: LineStatusDistributionDto[];
  periodChart: LinePeriodChartDto[];
  negotiationDistribution: LineNegotiationDistributionDto[];
}

export interface ConsolidatedDashboardDto {
  stats?: DashboardStatsDto;
  statusDistribution?: ContractStatusDistributionDto[];
  periodChart?: PeriodChartDto[];
  operationalChart?: OperationalChartDto[];
  financialChart?: FinancialChartDto[];
  bkoStats?: BkoDashboardStatsDto;
  stateTimeDistribution?: StateTimeDistributionDto[];
  topTeams?: TopTeamsResponseDto;
  topPerformers?: TopPerformersResponseDto;
  aprovadosCqvFinal?: number;
  recusadosCqv?: number;
  aprovadoCqv72h?: number;
  teamsPerformance?: TeamPerformanceDto[];
  vtmeOk?: number;
  linhasRenegociacaoAptas?: number;
  lines?: LinesDashboardDto;
}

export interface ContractListItemDto {
  id: string;
  clientName: string;
  clientCnpj: string;
  status: number | null;
  statusDisplay: string;
  dataVenda: string;
  dataAssinatura: string;
  valorTotalMensal: number;
  totalLinhas: number;
  tipoContratacao: string;
  userId: string;
  userName: string;
  userEmail: string;
  teamId?: string | null;
  teamName?: string | null;
  managerName?: string | null;
  vtmeConfirmado?: boolean | null;
  isGrouped: boolean;
  groupedCount: number;
  updatedAt: string;
  createdAt: string;
  linhasAptas?: number | null;
  linhasNaoAptas?: number | null;
  linhasAditivas?: number;
  receitaLinhasAditivas?: number;
  linhasRenegociacao?: number;
  receitaLinhasRenegociacao?: number;
  pontos?: number | null;
}

interface DashboardParams {
  days?: number;
  teamId?: string | null;
  status?: number | '';
}

class DashboardService extends BaseApiService {
  private async delay(ms: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  private isRetryableError(error: unknown): boolean {
    const axiosError = error as AxiosError<{ message?: string }>;
    const status = axiosError?.response?.status;

    // Falha de rede, timeout ou erro sem resposta (comum em VPS sob carga)
    if (!axiosError?.response) {
      return true;
    }

    // 5xx e 429 podem se recuperar com retry curto
    return status === 429 || (status !== undefined && status >= 500);
  }

  private async executeWithRetry<T>(
    operation: () => Promise<T>,
    retries: number = 1,
    retryDelayMs: number = 800
  ): Promise<T> {
    let attempt = 0;
    let lastError: unknown;

    while (attempt <= retries) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        if (attempt >= retries || !this.isRetryableError(error)) {
          throw error;
        }

        await this.delay(retryDelayMs * (attempt + 1));
        attempt += 1;
      }
    }

    throw lastError;
  }

  private buildParams(days?: number, teamId?: string | null, status?: number | ''): DashboardParams {
    const params: DashboardParams = {};
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    if (status !== undefined && status !== '') {
      params.status = status;
    }
    return params;
  }

  async getStats(days?: number, teamId?: string | null, status?: number | ''): Promise<DashboardStatsDto> {
    const params = this.buildParams(days, teamId, status);
    const response = await this.api.get<ApiResponse<DashboardStatsDto>>('/dashboard/stats', { params });
    return this.extractData(response);
  }

  async getContractStats(days?: number, teamId?: string | null): Promise<ContractStatsDto> {
    const params = this.buildParams(days, teamId);
    const response = await this.api.get<ApiResponse<ContractStatsDto>>('/dashboard/contract-stats', { params });
    return this.extractData(response);
  }

  async getBusinessMetrics(days?: number, teamId?: string | null): Promise<BusinessMetricsDto> {
    const params = this.buildParams(days, teamId);
    const response = await this.api.get<ApiResponse<BusinessMetricsDto>>('/dashboard/business-metrics', { params });
    return this.extractData(response);
  }

  async getPeriodMetrics(teamId?: string | null): Promise<PeriodMetricsDto> {
    const params = this.buildParams(undefined, teamId);
    const response = await this.api.get<ApiResponse<PeriodMetricsDto>>('/dashboard/period-metrics', { params });
    return this.extractData(response);
  }

  async getUserPerformance(teamId?: string | null): Promise<UserPerformanceDto[]> {
    const params = this.buildParams(undefined, teamId);
    const response = await this.api.get<ApiResponse<UserPerformanceDto[]>>('/dashboard/user-performance', { params });
    return this.extractData(response);
  }

  async getTeamPerformance(): Promise<TeamPerformanceDto[]> {
    const response = await this.api.get<ApiResponse<TeamPerformanceDto[]>>('/dashboard/team-performance');
    return this.extractData(response);
  }

  async getTeamsPerformance(days?: number, teamId?: string | null): Promise<TeamPerformanceDto[]> {
    const params: { days?: number; teamId?: string } = {};
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    const response = await this.api.get<ApiResponse<TeamPerformanceDto[]>>('/dashboard/teams-performance', { params });
    return this.extractData(response);
  }

  async getContractStatusDistribution(days?: number, teamId?: string | null, status?: number | ''): Promise<ContractStatusDistributionDto[]> {
    const params = this.buildParams(days, teamId, status);
    const response = await this.api.get<ApiResponse<ContractStatusDistributionDto[]>>('/dashboard/contract-status-distribution', { params });
    return this.extractData(response);
  }

  async getBusinessMetricsChart(teamId?: string | null): Promise<BusinessMetricsChartDto[]> {
    const params = this.buildParams(undefined, teamId);
    const response = await this.api.get<ApiResponse<BusinessMetricsChartDto[]>>('/dashboard/business-metrics-chart', { params });
    return this.extractData(response);
  }

  async getPeriodChart(days: number = 30, teamId?: string | null, status?: number | ''): Promise<PeriodChartDto[]> {
    const params = this.buildParams(days, teamId, status);
    const response = await this.api.get<ApiResponse<PeriodChartDto[]>>('/dashboard/period-chart', { params });
    return this.extractData(response);
  }

  async getTopPerformers(): Promise<TopPerformersDto> {
    const response = await this.api.get<ApiResponse<TopPerformersDto>>('/dashboard/top-performers');
    return this.extractData(response);
  }

  async getBkoDashboardStats(period?: number): Promise<BkoDashboardStatsDto> {
    const params: { days?: number } = {};
    if (period !== undefined && period !== null) {
      params.days = period;
    }
    const response = await this.api.get<ApiResponse<BkoDashboardStatsDto>>('/dashboard/bko-stats', { params });
    return this.extractData(response);
  }

  async getOperationalChart(period?: number): Promise<OperationalChartDto[]> {
    const params: { days?: number } = {};
    if (period !== undefined && period !== null) {
      params.days = period;
    }
    const response = await this.api.get<ApiResponse<OperationalChartDto[]>>('/dashboard/operational-chart', { params });
    return this.extractData(response);
  }

  async getFinancialChart(period?: number): Promise<FinancialChartDto[]> {
    const params: { days?: number } = {};
    if (period !== undefined && period !== null) {
      params.days = period;
    }
    const response = await this.api.get<ApiResponse<FinancialChartDto[]>>('/dashboard/financial-chart', { params });
    return this.extractData(response);
  }

  async getStateTimeDistribution(period?: number): Promise<StateTimeDistributionDto[]> {
    const params: { days?: number } = {};
    if (period !== undefined && period !== null) {
      params.days = period;
    }
    const response = await this.api.get<ApiResponse<StateTimeDistributionDto[]>>('/dashboard/state-time-distribution', { params });
    return this.extractData(response);
  }

  async getTopPerformersDetail(days?: number, teamId?: string | null): Promise<TopPerformersResponseDto> {
    const params: { days?: number; teamId?: string } = {};
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    const response = await this.api.get<ApiResponse<TopPerformersResponseDto>>('/dashboard/top-performers-detail', { params });
    return this.extractData(response);
  }

  async getTopTeamsDetail(days?: number): Promise<TopTeamsResponseDto> {
    const params: { days?: number } = {};
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    const response = await this.api.get<ApiResponse<TopTeamsResponseDto>>('/dashboard/top-teams-detail', { params });
    return this.extractData(response);
  }

  async getTeamSalespersons(teamId: string, days?: number, isBest?: boolean): Promise<TeamSalespersonsDto> {
    const params: { days?: number; isBest?: boolean } = {};
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (isBest !== undefined && isBest !== null) {
      params.isBest = isBest;
    }
    const response = await this.api.get<ApiResponse<TeamSalespersonsDto>>(`/dashboard/team-salespersons?teamId=${teamId}`, { params });
    return this.extractData(response);
  }

  async getAprovadoCqvUltimas72h(teamId?: string | null): Promise<number> {
    const params: { teamId?: string } = {};
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    const response = await this.api.get<ApiResponse<number>>('/dashboard/aprovado-cqv-ultimas-72h', { params });
    return this.extractData(response);
  }

  async getAprovadosCqvFinal(period: number, teamId?: string | null): Promise<number> {
    const params: { days: number; teamId?: string } = { days: period };
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    const response = await this.api.get<ApiResponse<number>>('/dashboard/aprovados-cqv-final', { params });
    return this.extractData(response);
  }

  async getRecusadosCqv(period: number, teamId?: string | null): Promise<number> {
    const params: { days: number; teamId?: string } = { days: period };
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    const response = await this.api.get<ApiResponse<number>>('/dashboard/recusados-cqv', { params });
    return this.extractData(response);
  }

  async getConsolidatedDashboard(
    days?: number,
    teamId?: string | null,
    startDate?: string,
    endDate?: string,
    options?: { bypassCache?: boolean }
  ): Promise<ConsolidatedDashboardDto> {
    const params: { days?: number; teamId?: string; startDate?: string; endDate?: string; bypassCache?: boolean } = {};
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (options?.bypassCache) {
      params.bypassCache = true;
    }

    const response = await this.executeWithRetry(
      () => this.api.get<ApiResponse<ConsolidatedDashboardDto>>('/dashboard/consolidated', { params }),
      1
    );
    return this.extractData(response);
  }

  async getVtmeOk(days?: number, teamId?: string | null): Promise<number> {
    const params: { days?: number; teamId?: string } = {};
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    const response = await this.api.get<ApiResponse<number>>('/dashboard/vtme-ok', { params });
    return this.extractData(response);
  }

  async getLinhasRenegociacaoAptas(days?: number, teamId?: string | null): Promise<number> {
    const params: { days?: number; teamId?: string } = {};
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    const response = await this.api.get<ApiResponse<number>>('/dashboard/linhas-aptas', { params });
    return this.extractData(response);
  }

  /** Serializa params para query string; arrays viram parâmetros repetidos (statuses=1&statuses=35) para binding no ASP.NET Core. */
  private paramsSerializerRepeat(params: Record<string, unknown>): string {
    return Object.entries(params)
      .filter(([, v]) => v != null && v !== '')
      .flatMap(([k, v]) =>
        Array.isArray(v)
          ? v.map((val) => `${encodeURIComponent(k)}=${encodeURIComponent(String(val))}`)
          : [`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`]
      )
      .join('&');
  }

  async getContractsByStatus(statuses?: number[], days?: number, teamId?: string | null, startDate?: string, endDate?: string): Promise<ContractListItemDto[]> {
    const params: Record<string, unknown> = {};
    if (statuses && statuses.length > 0) {
      params.statuses = statuses;
    }
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.executeWithRetry(
      () => this.api.get<ApiResponse<ContractListItemDto[]>>('/dashboard/contracts-by-status', {
        params,
        paramsSerializer: this.paramsSerializerRepeat.bind(this)
      }),
      1
    );
    return this.extractData(response);
  }

  async getContractsByLinhasAptas(days?: number, teamId?: string | null, startDate?: string, endDate?: string): Promise<ContractListItemDto[]> {
    const params: Record<string, unknown> = {};
    if (days !== undefined && days !== null) params.days = days;
    if (teamId) params.teamId = teamId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.executeWithRetry(
      () => this.api.get<ApiResponse<ContractListItemDto[]>>('/dashboard/contracts-by-linhas-aptas', { params }),
      1
    );
    return this.extractData(response);
  }

  async getContractsByVtmeConfirmed(teamId?: string | null, startDate?: string, endDate?: string): Promise<ContractListItemDto[]> {
    const params: Record<string, unknown> = {};
    if (teamId) params.teamId = teamId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.executeWithRetry(
      () => this.api.get<ApiResponse<ContractListItemDto[]>>('/dashboard/contracts-by-vtme-confirmed', { params }),
      1
    );
    return this.extractData(response);
  }

  async exportContractsByStatus(statuses?: number[], days?: number, teamId?: string | null, startDate?: string, endDate?: string): Promise<void> {
    const params: Record<string, unknown> = {};
    if (statuses && statuses.length > 0) {
      params.statuses = statuses;
    }
    if (days !== undefined && days !== null) {
      params.days = days;
    }
    if (teamId !== undefined && teamId !== null) {
      params.teamId = teamId;
    }
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await this.api.get('/dashboard/export-contracts', {
      params,
      paramsSerializer: this.paramsSerializerRepeat.bind(this),
      responseType: 'blob',
      timeout: 120000
    });
    
    // Criar link para download
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Gerar nome do arquivo
    const statusName = (statuses && statuses.length > 0) ? `status-${statuses.join('-')}` : 'todos';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.download = `contratos-${statusName}-${timestamp}.xlsx`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async exportContractsByVtmeConfirmed(teamId?: string | null, startDate?: string, endDate?: string): Promise<void> {
    const params: Record<string, unknown> = { vtmeConfirmed: true };
    if (teamId) params.teamId = teamId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.api.get('/dashboard/export-contracts', {
      params,
      paramsSerializer: this.paramsSerializerRepeat.bind(this),
      responseType: 'blob',
      timeout: 120000
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.download = `contratos-vtme-confirmado-${timestamp}.xlsx`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

const dashboardService = new DashboardService();
export default dashboardService;
